/**
 * SendReminderConfirmDialog — confirms a single-row OR bulk Send
 * Reminder action on the FU Requests tab.
 *
 * Mirrors Flutter `confirmDialog(...)` at `fu_requests_tab.dart:491-505`
 * (single) and `:680-707` (bulk). Both invocations ship the same
 * verbatim copy — the only difference is the array length.
 *
 * UX rules from Flutter (verbatim):
 *   - Title:  "Do you really want to send reminder?"
 *   - Body:   "We will inform the patient about this follow up request."
 *   - Confirm button → ships `{ fuRequestIds }` and closes on 200
 *
 * Wire shape verified against the 2026-05-21 dev curl. See
 * `useSendBulkReminder` for the endpoint contract.
 */

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSendBulkReminder } from '@/scheduling/services/use-send-bulk-reminder';

export interface SendReminderTarget {
  /** The IDs the user picked — 1 for kebab, N for bulk-select. Flutter
   *  doesn't distinguish at the wire level; both modes ship the same
   *  `fuRequestIds` array. */
  fuRequestIds: string[];
}

interface SendReminderConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SendReminderTarget | null;
  /** Called after a successful 200 so the caller can clear the
   *  bulk-selection set / kebab target. */
  onSuccess?: () => void;
}

export function SendReminderConfirmDialog({
  open,
  onOpenChange,
  target,
  onSuccess,
}: SendReminderConfirmDialogProps) {
  const sendMutation = useSendBulkReminder();

  const count = target?.fuRequestIds.length ?? 0;
  const canSubmit = count > 0 && !sendMutation.isPending;

  const submit = async () => {
    if (!target || target.fuRequestIds.length === 0) return;
    try {
      await sendMutation.mutateAsync({
        request: { fuRequestIds: target.fuRequestIds },
      });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Global QueryClient onError surfaces the failure as a toast.
      // Keep the dialog open so the user can retry.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <DialogHeader>
          <DialogTitle>Do you really want to send reminder?</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <p className="[font:var(--type-body)] text-[var(--ink-body)]">
            We will inform the patient about this follow up request.
          </p>
          {count > 1 ? (
            <p className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)]">
              {count} requests selected — a reminder will fire for each.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sendMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send reminder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
